import React, { ReactNode, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../Redux/store";
import metadata from "../../metadata.json";
import { setVersion } from "../../Redux/App/AppSlice";
import { clearSidebar } from "../../Redux/Sidebar/sidebarSlice";
import { Button, Result } from "antd";
import { clearAccess } from "../../Redux/Access/accessSlice";
import LoginFunction from "../../Functions/Login/Login";
import CustomerChooser from "../CustomerChooser/CustomerChooser";
import { useLocation, useNavigate } from "react-router-dom";
import GlobalLoader from "../GlobalLoader/GlobalLoader";
import { User, SuperAdminUser } from "../../Redux/User/userSlice";
import {
  closeCustomersModal,
  openCustomersModal,
} from "../../Redux/SuperAdminModal/modalSlice";


type PrivateRouteProps = {
  children: ReactNode;
  requireCustomer?: boolean;
  requireSuperAdmin?: boolean;
};

const PrivateRoute: React.FC<PrivateRouteProps> = ({
  children,
  requireCustomer,
  requireSuperAdmin,
}) => {
  const userData = useSelector((state: RootState) => state.user);
  const accessData = useSelector((state: RootState) => state.access);
  const currentVersion = useSelector((state: RootState) => state.app.version);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const revision = metadata;
  const [loadingPage, setLoadingPage] = useState(true);
  const [access, setAccess] = useState(false);
  const location = useLocation(); // Hook for tracking location changes

  interface Metadata {
    buildMajor: number;
    buildMinor: number;
    buildRevision: number;
    buildTag: string;
  }

  function compareMetadata(metadata1: Metadata, metadata2: Metadata): boolean {
    return (
      metadata1.buildMajor === metadata2.buildMajor &&
      metadata1.buildMinor === metadata2.buildMinor &&
      metadata1.buildRevision === metadata2.buildRevision &&
      metadata1.buildTag === metadata2.buildTag
    );
  }

  function isSuperAdminUser(user: User | SuperAdminUser | null): user is SuperAdminUser {
    return user !== null && (user as SuperAdminUser).groupAdmin !== undefined;
  }

  useEffect(() => {
    const checkAccess = async () => {
      if (!compareMetadata(currentVersion, revision)) {
        dispatch(setVersion(revision));
        dispatch(clearSidebar());
        dispatch(clearAccess(null));
        window.location.reload();
      } else {
        if (userData.token && accessData.accessToken) {
          try {
            const response = await LoginFunction.checkSession(userData.token, accessData.accessToken);
            if (response && response.status) {
              if (requireSuperAdmin && !isSuperAdminUser(userData.user)) {
                setAccess(false);
              } else {
                setAccess(response.status);
              }
            } else {
              setAccess(false);
              navigate("/");
            }
          } catch (error) {
            setAccess(false);
            navigate("/");
          } finally {
            setLoadingPage(false);
          }
        } else {
          setAccess(false);
          navigate("/");
        }
      }
    };

    checkAccess();

    if (requireCustomer) {
      dispatch(userData?.user?.custID ? closeCustomersModal() : openCustomersModal());
    }

  }, [location, userData]); // Run useEffect whenever the location changes

  const handleUnauthorized = () => {
    navigate("/user-management");
  };

  useEffect(() => {
    dispatch(closeCustomersModal());
    if (requireCustomer) {
      dispatch(
        userData?.user?.custID ? closeCustomersModal() : openCustomersModal()
      );
    }
  }, [userData, location]); // Run useEffect whenever userData or location changes

  useEffect(() => {
    if (requireSuperAdmin && !isSuperAdminUser(userData.user)) {
      setAccess(false);
    }
  }, [requireSuperAdmin, userData, location]); // Run useEffect whenever userData or location changes  

  if (loadingPage) {
    return <GlobalLoader />;
  }

  if (!access) {
    return (
      <Result
        status="403"
        title="403"
        subTitle="Sorry, you are not authorized to access this page."
        extra={
          <Button type="primary" onClick={handleUnauthorized}>
            Back Home
          </Button>
        }
      />
    );
  }

  return (
    <>
      {children}
      <CustomerChooser />
    </>
  );
};

export default PrivateRoute;
